import apiClient from '../apiClient';

import type { User } from '@/types/user';
import type { Account } from '@/types/account';

interface Credentials {
  email?: string;
  password?: string;
  [key: string]: any;
}

interface CurrentUserResponse {
  authenticated: boolean;
  user?: User;
  account?: Account;
}

const authApi = {
  /**
   * Login with email and password (cookie-based session)
   */
  async login(credentials: Credentials): Promise<any> {
    try {
      return await apiClient.post('/auth/login', credentials);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<any> {
    try {
      return await apiClient.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<CurrentUserResponse> {
    try {
      return await apiClient.get('/auth/me');
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  /**
   * Exchange Cial auth code for a local session
   */
  async exchangeCialCode(code: string, codeVerifier: string, nonce?: string | null): Promise<any> {
    try {
      return await apiClient.post('/auth/cial/exchange', { code, code_verifier: codeVerifier, nonce });
    } catch (error) {
      console.error('Cial exchange error:', error);
      throw error;
    }
  },

  /**
   * Record that the user is being sent to Cial's login page (login audit log)
   */
  async recordCialLoginAttempt(state: string): Promise<void> {
    await apiClient.post('/auth/cial/login-attempts', { state }, { timeout: 2000 });
  },

  /**
   * Get all accounts linked to the current user
   */
  async getMyAccounts(): Promise<any[]> {
    try {
      return await apiClient.get('/auth/accounts');
    } catch (error) {
      console.error('Get accounts error:', error);
      throw error;
    }
  },

  /**
   * Switch the active session account
   */
  async switchAccount(accountId: string): Promise<any> {
    try {
      return await apiClient.post(`/auth/switch-account/${accountId}`, {});
    } catch (error) {
      console.error('Switch account error:', error);
      throw error;
    }
  },

  /**
   * Change current user password
   */
  async changePassword(newPassword: string, currentPassword: string): Promise<any> {
    try {
      return await apiClient.post('/auth/change-password', { 
        new_password: newPassword,
        current_password: currentPassword
      });
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  },

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<any> {
    try {
      return await apiClient.post('/auth/refresh', {});
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }
};

export default authApi;
